/**
 * SF-4758: sf-api calls for the product priorities pages. Same pattern as
 * src/pages/demos/api/demoBuildsClient.ts: thin hooks over Refine's useCustom / useCustomMutation,
 * whose data provider unwraps the sf-api `{ success, data }` envelope, so the inner payload is
 * `query.data?.data`. The exceptions are useResults (a plain useQuery whose data is a
 * ResultsAnswer) and useJiraEpics (a useInfiniteQuery of JiraEpicsPage), see there. Every route is under /admin/v1/priorities:
 *
 *   GET  /me                     who I am here: voter, manage, insights, N
 *   GET  /items?status=          open (default) | done | removed | all
 *   POST /items                  add (manage)
 *   PATCH /items/:id             edit (manage)
 *   POST /items/:id/status       open | done | removed (manage)
 *   GET  /me/ballot, PUT /me/ballot   my ranking (voters only)
 *   GET  /results?team=          combined ranking (team filter for insights)
 *   GET  /participation          X of Y have ranked (+ people for insights)
 *   GET  /ballots/:email         one person's ranking (insights)
 *   GET  /settings               N (+ weights for insights)
 *   PUT  /settings/top-n         change N (manage)
 *   PUT  /settings/weights       team and person weights (insights)
 *   GET  /jira/epics?q=&cursor=  open Jira tickets to pick from, newest first (manage, SF-4794)
 *
 * SF-5080: lists and suggestions. Every list-scoped read takes ?list= (a list id) and
 * every list-scoped write takes listId in its body (PUT /me/ballot takes ?list=):
 *   GET  /lists, POST /lists, PATCH /lists/:id, POST /lists/:id/status   lists (writes: manage)
 *   DELETE /lists/:id            delete an archived list with everything in it (manage, SF-5087)
 *   GET  /companies?q=           active Paid and Demo companies to pick from (SF-5087)
 *   POST /suggestions, GET /suggestions/mine?cursor=                     anyone allowed
 *   POST /suggestions/:id/accept|merge|decline, POST /suggestions/combine (manage)
 *   POST /suggestions/eva-groupings                                      Eva's proposals (manage)
 *
 * The server decides every permission; the pages only hide what the caller cannot use.
 */
import { useCallback } from 'react';
import {
  useCustom,
  useCustomMutation,
  useDataProvider,
  useOnError,
  type BaseRecord,
} from '@refinedev/core';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { TeamKey } from '../../../utils/teams';

export const PRIORITIES_BASE = 'admin/v1/priorities';

export type ItemStatus = 'open' | 'done' | 'removed';

export type ListStatus = 'hidden' | 'voting' | 'archived';

export interface PriorityList {
  id: string;
  name: string;
  description: string;
  status: ListStatus;
  createdAt: string | null;
  openItemCount: number;
  /** Only for manage users. */
  pendingSuggestionCount?: number;
}

export type ItemSource = 'admin' | 'suggestion';

export interface PrioritiesMe {
  email: string;
  name: string | null;
  team: TeamKey | null;
  isVoter: boolean;
  hasSubmitted: boolean;
  canManage: boolean;
  canInsights: boolean;
  topN: number;
  /**
   * SF-5087: the list asked for or, with none asked for, the oldest list open for voting. The
   * page reads its list from here when the URL names none.
   */
  list?: Pick<PriorityList, 'id' | 'name' | 'description' | 'status' | 'createdAt'>;
}

/** SF-5087: a company that wants an item or suggestion. */
export interface PriorityCompany {
  id: string;
  name: string;
}

export interface JiraLink {
  key: string;
  url: string;
}

export interface PriorityItem {
  id: string;
  title: string;
  description: string | null;
  /**
   * SF-5087: the Jira tickets this item tracks (up to 10); the item closes once every one of
   * them is done in Jira. jiraLinks gives each key's link.
   */
  jiraKeys: string[];
  jiraLinks: JiraLink[];
  companies?: PriorityCompany[];
  status: ItemStatus | 'suggested' | 'merged' | 'declined';
  /** SF-5080: which list the item is in, where it came from, how many suggestions it took in. */
  listId?: string;
  source?: ItemSource;
  suggestedCount?: number;
  createdByEmail: string | null;
  createdAt: string;
  completedAt: string | null;
  /** 'jira' when the item was closed because its epic finished in Jira. */
  completedBy?: string | null;
  removedAt: string | null;
}

export interface Ballot {
  itemIds: string[];
  updatedAt: string | null;
  newItemsSinceUpdate: number;
}

export interface ResultItem {
  id: string;
  title: string;
  description: string | null;
  jiraLinks?: JiraLink[];
  /** SF-5104: the companies that want it; shown only, never part of the scoring. */
  companies?: PriorityCompany[];
  rank: number;
  points: number;
  voterCount: number;
}

export interface Results {
  topN: number;
  team: TeamKey | null;
  voterCount: number;
  items: ResultItem[];
}

export type WeightSource = 'person' | 'team' | 'default';

export interface Participant {
  email: string;
  name: string | null;
  team: TeamKey | null;
  submitted: boolean;
  updatedAt: string | null;
  weight: number;
  weightSource: WeightSource;
}

export interface Participation {
  eligible: number;
  submitted: number;
  /** Only for insights users. */
  people?: Participant[];
}

export interface PersonBallot {
  email: string;
  name: string | null;
  team: TeamKey | null;
  itemIds: string[];
  updatedAt: string | null;
}

export interface PersonWeight {
  email: string;
  weight: number;
}

export interface PrioritiesSettings {
  topN: number;
  /** Only for insights users. */
  teamWeights?: Record<TeamKey, number>;
  personWeights?: PersonWeight[];
}

export interface ItemInput {
  title?: string;
  description?: string | null;
  jiraKeys?: string[];
  companyIds?: string[];
  /** SF-5080: the list to add the item to, or to move it to. */
  listId?: string;
}

export type SuggestionStatus = 'suggested' | 'accepted' | 'merged' | 'declined';

/**
 * SF-5080: one of my suggestions and what happened to it (GET /suggestions/mine). `item` is the
 * item it became (accepted) or was folded into (merged); null while waiting or once declined.
 */
export interface MySuggestion {
  id: string;
  title: string;
  description: string;
  listId: string;
  status: SuggestionStatus;
  companies?: PriorityCompany[];
  createdAt: string;
  decidedAt: string | null;
  item: { id: string; title: string } | null;
}

/** One page of my suggestions, newest first; nextCursor reads the next (older) page. */
export interface MySuggestionsPage {
  suggestions: MySuggestion[];
  nextCursor: string | null;
}

/** SF-5080: one of Eva's proposed items. Nothing is saved until a manager acts on it. */
export interface EvaGroup {
  title: string;
  description: string;
  listId: string | null;
  suggestionIds: string[];
  existingItemId: string | null;
  reason: string;
}

export interface JiraEpic {
  key: string;
  title: string;
  description: string | null;
  url: string;
  updatedAt: string | null;
  /** SF-5080: any ticket can be picked now, so the row shows its type and status. */
  issueType?: string | null;
  status?: string | null;
  /** The open item already tracking this epic, if any: it cannot be picked again. */
  itemId: string | null;
}

export interface JiraEpicsPage {
  epics: JiraEpic[];
  nextCursor: string | null;
}

export interface WeightsInput {
  teamWeights?: Partial<Record<TeamKey, number>>;
  personWeights?: Array<{ email: string; weight: number | null }>;
}

/** Every priorities read lives under this key, so one invalidate refreshes the whole page. */
const KEY_ROOT = ['sf-admin', 'priorities'] as const;
const key = (...parts: unknown[]) => [...KEY_ROOT, ...parts];

function usePrioritiesGet<T extends BaseRecord>(
  path: string,
  queryKey: unknown[],
  enabled = true,
  query?: Record<string, string>,
) {
  return useCustom<T>({
    url: `/${PRIORITIES_BASE}${path}`,
    method: 'get',
    config: query ? { query } : undefined,
    // A 403 here is an answer (not a voter, not submitted yet), not a blip: never retry it.
    // Refine keeps the previous key's data by default; a new key (another list, another person)
    // shows its own loading state instead, so one list's answer is never shown as another's.
    queryOptions: {
      queryKey,
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    },
    errorNotification: false,
  });
}

// SF-5080: every list-scoped key carries the list id and no read keeps the previous key's data,
// so switching list never shows the old list's answer while the new one loads.
/** SF-5087: with no list, sf-api answers for the oldest list open for voting (see `list`). */
export const usePrioritiesMe = (listId?: string) =>
  usePrioritiesGet<PrioritiesMe>(
    '/me',
    key('me', listId ?? 'default'),
    true,
    listId ? { list: listId } : undefined,
  );

export const usePriorityLists = () =>
  usePrioritiesGet<{ lists: PriorityList[] }>('/lists', key('lists'));

export const usePriorityItems = (
  status: ItemStatus | 'all' | 'suggested',
  enabled = true,
  listId: string,
) =>
  usePrioritiesGet<{ items: PriorityItem[] }>('/items', key('items', listId, status), enabled, {
    status,
    list: listId,
  });

/**
 * My suggestions, one page (50, newest first) per cursor; the table's Load more reads the next.
 * Same shape as useJiraEpics: a write refreshes every page loaded so far.
 */
export const useMySuggestions = () => {
  const getDataProvider = useDataProvider();
  // Same as useResults: failures go to the auth provider like every useCustom read.
  const { mutate: checkError } = useOnError();
  return useInfiniteQuery<MySuggestionsPage>({
    queryKey: key('suggestions-mine'),
    queryFn: async ({ pageParam }) => {
      const custom = getDataProvider().custom;
      if (!custom) throw new Error('The data provider has no custom request.');
      const { data } = await custom<MySuggestionsPage>({
        url: `/${PRIORITIES_BASE}/suggestions/mine`,
        method: 'get',
        query: typeof pageParam === 'string' ? { cursor: pageParam } : undefined,
      });
      return data;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: false,
    refetchOnWindowFocus: false,
    onError: (error) => checkError(error),
    keepPreviousData: false,
  });
};

export const ballotQueryKey = (listId: string) => key('ballot', listId);

export const useMyBallot = (enabled: boolean, listId: string) =>
  usePrioritiesGet<Ballot>('/me/ballot', ballotQueryKey(listId), enabled, { list: listId });

export const resultsQueryKey = (team: TeamKey | null, listId: string) =>
  key('results', listId, team ?? 'everyone');

/**
 * The results read's answer. NOT_SUBMITTED is an answer (the person has no saved ranking), so it
 * is stored as data like real results rather than thrown as an error. A later failed read (a 500)
 * then leaves the last answer in place, whichever it was: no marker, counter or clock to keep in
 * step with the query, and the answer lives and dies with the query itself.
 */
export type ResultsAnswer = { kind: 'results'; results: Results } | { kind: 'notSubmitted' };

export const useResults = (team: TeamKey | null, enabled = true, listId: string) => {
  const getDataProvider = useDataProvider();
  // useCustom passes failures to the auth provider (a 401 that cannot be refreshed logs out);
  // this query does the same.
  const { mutate: checkError } = useOnError();
  return useQuery<ResultsAnswer>({
    queryKey: resultsQueryKey(team, listId),
    queryFn: async () => {
      const custom = getDataProvider().custom;
      if (!custom) throw new Error('The data provider has no custom request.');
      try {
        const { data } = await custom<Results>({
          url: `/${PRIORITIES_BASE}/results`,
          method: 'get',
          query: team ? { team, list: listId } : { list: listId },
        });
        return { kind: 'results', results: data };
      } catch (error) {
        if (prioritiesErrorCode(error) === 'NOT_SUBMITTED') return { kind: 'notSubmitted' };
        throw error;
      }
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    onError: (error) => checkError(error),
    // A team switch never shows another team's table while it loads.
    keepPreviousData: false,
  });
};

const JIRA_EPICS_KEY = key('jira-epics');
export const jiraEpicsQueryKey = (q: string, listId: string) => [...JIRA_EPICS_KEY, listId, q];

/**
 * SF-4794: open Jira epics for the Add item window, one page per cursor. Every priorities
 * write drops these lists (see usePrioritiesWrite), so the "Already on the list" marks are
 * read again after it. Never
 * retried, like every priorities read: 503 JIRA_NOT_CONFIGURED and the 4xx are answers, and a
 * 502 gets the Retry button.
 */
export const useJiraEpics = (q: string, enabled = true, listId: string) => {
  const getDataProvider = useDataProvider();
  // Same as useResults: failures go to the auth provider like every useCustom read.
  const { mutate: checkError } = useOnError();
  return useInfiniteQuery<JiraEpicsPage>({
    queryKey: jiraEpicsQueryKey(q, listId),
    queryFn: async ({ pageParam }) => {
      const custom = getDataProvider().custom;
      if (!custom) throw new Error('The data provider has no custom request.');
      // The list decides which tickets are "Already on the list".
      const query: Record<string, string> = { list: listId };
      if (q) query.q = q;
      if (typeof pageParam === 'string') query.cursor = pageParam;
      const { data } = await custom<JiraEpicsPage>({
        url: `/${PRIORITIES_BASE}/jira/epics`,
        method: 'get',
        query,
      });
      return data;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    onError: (error) => checkError(error),
    // Refine keeps the previous key's data by default; a new search shows its own loading
    // state instead, so the old search's rows (and paging) are never taken for the new one's.
    keepPreviousData: false,
  });
};

export const useParticipation = (enabled = true, listId: string) =>
  usePrioritiesGet<Participation>('/participation', key('participation', listId), enabled, {
    list: listId,
  });

export const usePersonBallot = (email: string | null, listId: string) =>
  usePrioritiesGet<PersonBallot>(
    `/ballots/${encodeURIComponent(email ?? '')}`,
    key('person-ballot', listId, email),
    !!email,
    { list: listId },
  );

/**
 * SF-5087: companies to pick from (active, Paid or Demo; sf-api filters and caps at 50). Kept
 * between searches so the picker never flashes empty while the next search loads.
 */
export const usePriorityCompanies = (q: string, enabled = true) =>
  useCustom<{ companies: PriorityCompany[] }>({
    url: `/${PRIORITIES_BASE}/companies`,
    method: 'get',
    config: q ? { query: { q } } : undefined,
    queryOptions: {
      queryKey: key('companies', q),
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
    },
    errorNotification: false,
  });

export const usePrioritiesSettings = (enabled: boolean) =>
  usePrioritiesGet<PrioritiesSettings>('/settings', key('settings'), enabled);

const quiet = { errorNotification: false as const, successNotification: false as const };
const write = (method: 'post' | 'put' | 'patch' | 'delete', path: string, values: object) => ({
  url: `/${PRIORITIES_BASE}${path}`,
  method,
  values,
  ...quiet,
});
const itemPath = (id: string) => `/items/${encodeURIComponent(id)}`;
const listPath = (id: string) => `/lists/${encodeURIComponent(id)}`;
const suggestionPath = (id: string) => `/suggestions/${encodeURIComponent(id)}`;

// Refine's custom mutation takes no query, so the list goes in the path.
const ballotPath = (listId: string) => `/me/ballot?list=${encodeURIComponent(listId)}`;
export const saveBallotArgs = (itemIds: string[], listId: string) => ({
  ...write('put', ballotPath(listId), { itemIds }),
  listId,
});
export const createListArgs = (values: { name: string; description?: string }) =>
  write('post', '/lists', values);
export const updateListArgs = (id: string, values: { name?: string; description?: string }) =>
  write('patch', listPath(id), values);
/** SF-5087: sf-api refuses (409 LIST_NOT_ARCHIVED) unless the list is archived. */
export const deleteListArgs = (id: string) => write('delete', listPath(id), {});
export const setListStatusArgs = (id: string, status: ListStatus) =>
  write('post', `${listPath(id)}/status`, { status });
export const createSuggestionArgs = (values: {
  title: string;
  description?: string;
  listId?: string;
  companyIds?: string[];
}) => write('post', '/suggestions', values);
// The item form sends an emptied field as null, which clears it (sf-api reads a null
// description as empty); a field left out keeps the suggestion's own. So only unset (undefined)
// fields are dropped.
const setFields = (values: ItemInput) =>
  Object.fromEntries(Object.entries(values).filter(([, v]) => v !== undefined));
export const acceptSuggestionArgs = (id: string, values: ItemInput) =>
  write('post', `${suggestionPath(id)}/accept`, setFields(values));
export const mergeSuggestionArgs = (id: string, itemId: string) =>
  write('post', `${suggestionPath(id)}/merge`, { itemId });
export const declineSuggestionArgs = (id: string) =>
  write('post', `${suggestionPath(id)}/decline`, {});
// Every id Eva grouped is sent (sf-api takes 1 to 200); combine is all or nothing, answering
// 409 SUGGESTION_ALREADY_DECIDED with nothing changed when any of them was decided meanwhile.
export const combineSuggestionsArgs = (suggestionIds: string[], values: ItemInput) =>
  write('post', '/suggestions/combine', { suggestionIds, description: '', ...setFields(values) });
/** The signal lets Cancel stop the wait (the data provider passes meta.signal to the request). */
export const evaGroupingsArgs = (listId: string, signal: AbortSignal) => ({
  ...write('post', '/suggestions/eva-groupings', { listId }),
  meta: { signal },
});
export const createItemArgs = (values: ItemInput) => write('post', '/items', values);
export const updateItemArgs = (id: string, values: ItemInput) =>
  write('patch', itemPath(id), values);
export const setItemStatusArgs = (id: string, status: ItemStatus) =>
  write('post', `${itemPath(id)}/status`, { status });
export const setTopNArgs = (topN: number) => write('put', '/settings/top-n', { topN });
export const setWeightsArgs = (values: WeightsInput) => write('put', '/settings/weights', values);

const isJiraEpicsKey = (queryKey: readonly unknown[]) =>
  JIRA_EPICS_KEY.every((part, i) => queryKey[i] === part);

/** Reads whose answer depends on whether I have saved a ranking. */
const BALLOT_DEPENDENT_KEYS = [key('results'), key('participation')];

/**
 * A priorities write, then a refresh of every priorities read: a change to one part (an item
 * closing, N changing, a weight) moves the ranking, results and counts everywhere else.
 *
 * After a ranking save, invalidating is not enough for results: an unmounted Results tab keeps
 * its cached answer and shows it again while refetching, so a voter who just emptied their
 * ranking would see the combined results the server now refuses (NOT_SUBMITTED). resetQueries
 * drops that cached data (and cancels any fetch begun before the save), so results only ever
 * render from an answer fetched after the latest save; a mounted reader refetches at once.
 * The Jira epic lists are dropped the same way, without waiting (see below).
 */
export function usePrioritiesWrite() {
  const queryClient = useQueryClient();
  const mutation = useCustomMutation();
  const { mutateAsync } = mutation;
  const run = useCallback(
    async (args: ReturnType<typeof write> & { listId?: string }, { ballotSaved = false } = {}) => {
      const { listId: savedList, ...request } = args;
      const result = await mutateAsync(request);
      if (ballotSaved && savedList) {
        // The save answers with the stored ranking: show that, so a failed refresh below can
        // never put the pre-save ranking back on screen (and a later save cannot send it).
        // A ballot read begun before the save would land after this and put the old ranking
        // back, so stop it first; the refetch below starts after the save.
        await queryClient.cancelQueries({ queryKey: ballotQueryKey(savedList) });
        queryClient.setQueryData(ballotQueryKey(savedList), result);
        await Promise.all(
          BALLOT_DEPENDENT_KEYS.map((queryKey) => queryClient.resetQueries({ queryKey })),
        );
      }
      // The Jira epic lists are optional and slow (every loaded page is another Jira search),
      // so the write does not wait for them. An epic read begun before the write carries the
      // old "Already on the list" marks and would land after it, so stop those first; then
      // drop every list, so an open picker reads just its first page again and a closed one
      // reads afresh when the window next opens.
      await queryClient.cancelQueries({ queryKey: JIRA_EPICS_KEY });
      void queryClient.resetQueries({ queryKey: JIRA_EPICS_KEY });
      await queryClient.invalidateQueries({
        queryKey: KEY_ROOT,
        predicate: (query) => !isJiraEpicsKey(query.queryKey),
      });
      return result;
    },
    [mutateAsync, queryClient],
  );
  return { run, isLoading: mutation.isLoading };
}

/** The sf-api `code` of a failed call, if any. */
export function prioritiesErrorCode(error: unknown): string | undefined {
  return (error as AxiosError<{ code?: string }>)?.response?.data?.code;
}

/** Plain words for the sf-api codes a person can cause; anything else gets the fallback. */
const ERROR_MESSAGES: Record<string, string> = {
  NOT_A_VOTER: 'You can rank priorities once an admin sets your team.',
  TOO_MANY_ITEMS: 'You have ranked more items than allowed. Remove some and save again.',
  JIRA_TICKET_ALREADY_LISTED: 'One of those Jira tickets is already an open item in this list.',
  COMPANY_NOT_ALLOWED: 'One of the companies cannot be picked. Remove it and try again.',
  // Accept, fold in and combine refuse a result with more companies than an item may hold (20).
  TOO_MANY_COMPANIES:
    'That would give the item more than 20 companies. Remove some from the item first.',
  LIST_NOT_ARCHIVED: 'Archive the list before deleting it.',
  UNKNOWN_ITEM: 'One of the items no longer exists. Reload the page and save again.',
  ITEM_NOT_FOUND: 'That item no longer exists. Reload the page.',
  VALIDATION_ERROR: 'Some of the values are not valid. Check them and try again.',
  STAFF_DIRECTORY_UNAVAILABLE: 'The staff list could not be read just now. Try again shortly.',
  // sf-api caps open items at 200 (add item and reopen both answer this at the cap).
  TOO_MANY_OPEN_ITEMS: 'There are already 200 open items. Mark some done or remove them first.',
  FORBIDDEN: 'You do not have permission to do that.',
  JIRA_EPIC_ALREADY_LISTED: 'That Jira ticket is already an open item in this list.',
  JIRA_NOT_CONFIGURED: 'The Jira list is not set up yet. You can still add the item by hand.',
  JIRA_AUTH_FAILED: 'Jira refused our login. Ask an admin to check it.',
  JIRA_UNAVAILABLE: 'Jira could not be reached just now. Try again shortly.',
  INTERNAL_ERROR: 'Something went wrong on our side. Try again shortly.',
  LIST_NOT_FOUND: 'That list no longer exists. Pick another list.',
  LIST_NOT_VOTING: 'This list is not open for voting, so rankings cannot be saved.',
  LIST_ARCHIVED: 'This list is archived.',
  // sf-api caps how many lists there can be (archived ones count too).
  TOO_MANY_LISTS: 'There are already as many lists as allowed.',
  // sf-api caps waiting suggestions at 20 per person and 500 overall.
  TOO_MANY_SUGGESTIONS: 'You have 20 suggestions waiting. Wait for some to be looked at first.',
  TOO_MANY_SUGGESTIONS_OVERALL: 'There are too many suggestions waiting. Try again later.',
  SUGGESTION_ALREADY_DECIDED: 'Someone has already dealt with that suggestion. Reload the page.',
  EVA_UNAVAILABLE: 'Eva could not be reached just now. Try again shortly.',
};

/**
 * SF-5087: a company sf-api left off. `name` is null when the company itself no longer exists.
 */
export interface DroppedCompany {
  id: string;
  name: string | null;
}

/**
 * SF-5087: accept, fold in and combine leave off the suggestion's companies that are no longer
 * active Paid or Demo customers, and name them in droppedCompanies. The forms start with the
 * suggestions' companies and send the full wanted set: sf-api drops (not refuses) ones already
 * on the suggestion. `result` is what usePrioritiesWrite's run returned.
 */
export function droppedCompanies(result: unknown): DroppedCompany[] {
  const dropped = (result as { data?: { droppedCompanies?: DroppedCompany[] } } | undefined)?.data
    ?.droppedCompanies;
  return Array.isArray(dropped) ? dropped : [];
}

const MISSING_COMPANY = 'A company that no longer exists';
const MISSING_COMPANIES = 'companies that no longer exist';

/** Plain words for the companies left off (one write's or several), or null for none. */
export function droppedCompaniesMessage(companies: DroppedCompany[]): string | null {
  // By id: two companies that no longer exist both have no name, but are still two.
  const unique = [...new Map(companies.map((c) => [c.id, c])).values()];
  if (unique.length === 0) return null;
  if (unique.length === 1) {
    return `${unique[0].name ?? MISSING_COMPANY} was left off: it is no longer an active paid or demo customer.`;
  }
  const named = unique.flatMap((c) => (c.name === null ? [] : [c.name]));
  const missing = unique.length - named.length;
  const parts =
    missing === 0
      ? named
      : [
          ...named,
          missing === 1 ? 'a company that no longer exists' : `${missing} ${MISSING_COMPANIES}`,
        ];
  const words =
    parts.length === 1
      ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `${words} were left off: they are no longer active paid or demo customers.`;
}

export function prioritiesErrorMessage(error: unknown, fallback: string): string {
  const code = prioritiesErrorCode(error);
  if (code === 'VALIDATION_ERROR') {
    // The server names the bad field, which helps more than the generic sentence.
    return (
      (error as AxiosError<{ message?: string }>)?.response?.data?.message ??
      ERROR_MESSAGES.VALIDATION_ERROR
    );
  }
  return (code && ERROR_MESSAGES[code]) || fallback;
}
